import './ExternalOfferEmailStyle.less';

import { DropdownSelect, Loader, Modal } from '@opendoor/bricks';
import Mustache from 'mustache';
import * as React from 'react';
import { MouseEventHandler } from 'react';
import { RouteComponentProps } from 'react-router';
import { Option } from 'react-select';

import { fetchOffer, sendEmail } from '../externalTooling/api';
import { offerTemplates } from './offer-email-templates.mustache';

interface IDataResponse {
  lead: { first_name: string };
  offer: {
    acq_cx_lead: {
      display_phone_number: string;
      full_name: string;
      name: string;
    };
    email: string;
    has_outstanding_valuations: false;
    show_offer_email: boolean;
    show_warning: boolean;
    state: string;
    valuation: string;
  };
}

export interface IState {
  badToken: boolean;
  data: IDataResponse;
  isDismissed: boolean;
  isSuccess: boolean;
  loading: boolean;
  selectedTemplateLabel: string;
  sentOffer: boolean;
  templateOptions: Option[];
}

interface IExternalOfferEmailParams {
  token: string;
}

export type IProps = RouteComponentProps<IExternalOfferEmailParams>;

function getTemplateOptions() {
  return offerTemplates.map(template => {
    return { label: template['label'], value: template['label'] };
  });
}

function getIndex(templateLabel) {
  return offerTemplates.findIndex(item => item.label === templateLabel);
}

class ExternalOfferEmail extends React.Component<IProps, IState> {
  public state: IState = {
    badToken: false,
    data: {
      lead: { first_name: '' },
      offer: {
        acq_cx_lead: {
          display_phone_number: '',
          full_name: '',
          name: '',
        },
        email: '',
        has_outstanding_valuations: false,
        show_offer_email: false,
        show_warning: false,
        state: '',
        valuation: '',
      },
    },
    isDismissed: true,
    isSuccess: false,
    loading: true,
    selectedTemplateLabel: 'After Offer call',
    sentOffer: false,
    templateOptions: [],
  };

  public componentDidMount() {
    fetchOffer(this.props.match.params.token).then(res => {
      if (res.error) {
        this.setState({
          badToken: true,
          loading: false,
        });
      } else {
        this.setState({
          data: res.data,
          loading: false,
          selectedTemplateLabel: 'After Offer call',
          sentOffer: false,
          templateOptions: getTemplateOptions(),
        });
      }
    });
  }

  public render() {
    const {
      badToken,
      data,
      loading,
      selectedTemplateLabel,
      templateOptions,
      sentOffer,
      isDismissed,
      isSuccess,
    } = this.state;
    const {
      state: state,
      show_warning: showWarning,
      has_outstanding_valuations: hasVals,
      show_offer_email: showOfferEmail,
    } = data.offer;
    if (badToken) {
      return (
        <div className="no-offer">
          <p>
            An offer for this flip token ({this.props.match.params.token}) does not exist. If you
            believe this is in error, please reach out to #salesforce-questions.
          </p>
        </div>
      );
    }
    const selectedTemplateMessage = offerTemplates[getIndex(selectedTemplateLabel)].message;
    const compiledTemplate = Mustache.render(selectedTemplateMessage, data);
    const buttonText =
      (sentOffer === true || state === 'sent' || state === 'viewing' ? 'Re-' : '') +
      'Send Offer Email';
    if (loading) {
      return <Loader />;
    }

    if (!showOfferEmail) {
      return (
        <div className="no-offer">
          <p>
            An offer cannot be sent at this time. If you believe this is in error, please reach out
            to #salesforce-questions.
          </p>
        </div>
      );
    }
    return (
      <div className="od-compose-offer-page">
        <div className="container">
          <div className="row">
            {hasVals && <div className="col-xs-9 alert-warning">Outstanding Valuations</div>}
          </div>
          <div className="row">
            <div className="col-xs-12">
              <Modal
                className="od-offer-send-modal"
                isOpen={!isDismissed}
                contentLabel="Offer Confirmation"
              >
                {isSuccess && <h4 className="success-fail">Offer was successfully sent!</h4>}
                {!isSuccess && (
                  <h4 className="success-fail">
                    {' '}
                    Something went wrong and the offer was not successfully sent. Please reach out
                    to #salesforce-questions.
                  </h4>
                )}
                <button className="button secondary" onClick={this.closeModal}>
                  Close
                </button>
              </Modal>
            </div>
          </div>
          <div className="row">
            <div className="col-xs-12 od-dropdown-select">
              <DropdownSelect
                onChange={this.onTemplateOptionsChange}
                options={templateOptions}
                selectedValue={selectedTemplateLabel}
                title="Select Offer Template:"
              />
            </div>
          </div>
          <div className="row">
            <div className="col-xs-12 od-react-mustache">
              <textarea className="od-text-area" value={compiledTemplate} />
            </div>
          </div>
          <div className="row">
            {showWarning && (
              <div className="col-xs-9 alert-danger">To be sent by CX after phone call.</div>
            )}
          </div>
          <div className="row">
            <div className="col-xs-12 od-send-email">
              <a
                className="button secondary send-offer-email-button"
                onClick={this.sendOfferEmail(compiledTemplate)}
              >
                <span className="emoji-wrapper">📬</span> {buttonText}
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  private onTemplateOptionsChange = (newValue: string) => {
    this.setState({ selectedTemplateLabel: newValue });
  };

  private closeModal = () => this.setState({ isDismissed: true });

  private sendOfferEmail = (compiledTemplate: string): MouseEventHandler<HTMLAnchorElement> => {
    return () => {
      const { token } = this.props.match.params;
      const { email, valuation } = this.state.data.offer;
      const confirmText = `Send offer to ${email} with valuation ${valuation}?`;
      if (confirm(confirmText)) {
        this.setState({ loading: true }, () => {
          sendEmail(token, compiledTemplate).then(res => {
            this.setState({
              isDismissed: false,
              isSuccess: res.ok,
              loading: false,
              sentOffer: true,
            });
          });
        });
      }
    };
  };
}

export default ExternalOfferEmail;
